import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { enGB as enDates, et as etDates } from 'date-fns/locale';
import { enGB as enDays, et as etDays } from '@daypicker/react/locale';

/** A civil date as the API sends it: `2026-10-18` (docs/02 §5). */
export type IsoDate = string;

/** The date an ISO string names, at local midnight; `undefined` when it names none. */
export function toDate(iso: IsoDate): Date | undefined {
  const date = parseISO(iso);
  return isValid(date) ? date : undefined;
}

/** The ISO string for a picked date. */
export function toIso(date: Date): IsoDate {
  return format(date, 'yyyy-MM-dd');
}

/** How a date reads in the UI: `18 Oct 2026`, `18. okt 2026`. */
export function showDate(date: Date, language: string): string {
  return format(date, 'd MMM yyyy', { locale: language === 'et' ? etDates : enDates });
}

/** Days from one date to another, the way a stay counts them (18 → 28 Oct is 10). */
export function daysBetween(from: Date, to: Date): number {
  return differenceInCalendarDays(to, from);
}

/** The calendar's locale: British English (Monday first) or Estonian. */
export function dayLocale(language: string) {
  return language === 'et' ? etDays : enDays;
}

/** A word as it is compared: lower case, without its accents, marks or points — `Märts.` as
 * `marts`, `ต.ค.` as `ตค`. */
function bare(word: string): string {
  return word
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/** Every language with a two-letter code the browser's own CLDR knows: none listed by hand. */
function languages(): string[] {
  const letters = Array.from({ length: 26 }, (_, at) => String.fromCharCode(97 + at));
  const codes = letters.flatMap((a) => letters.map((b) => a + b));
  return Intl.DateTimeFormat.supportedLocalesOf(codes);
}

/** Each month's names in every language, long and short, alone and after a day (`toukokuuta`),
 * made once when first asked for. */
let monthNames: [string, number][] | null = null;
function months(): [string, number][] {
  monthNames ??= languages()
    .flatMap((language) =>
      Array.from({ length: 12 }, (_, month) => {
        const day = new Date(2026, month, 16);
        const said = (options: Intl.DateTimeFormatOptions) =>
          new Intl.DateTimeFormat(language, options)
            .formatToParts(day)
            .filter((part) => part.type === 'month')
            .map((part) => bare(part.value));
        return [
          ...said({ month: 'long' }),
          ...said({ month: 'short' }),
          ...said({ day: 'numeric', month: 'long' }),
          ...said({ day: 'numeric', month: 'short' }),
        ].map((name): [string, number] => [name, month + 1]);
      }).flat(),
    )
    .filter(([name]) => name.length >= 2 && !/\p{N}/u.test(name));
  return monthNames;
}

/** A year as the common calendar counts it: the Buddhist calendar's, as Thailand writes it
 * (`2569`), 543 years on. */
const commonYear = (year: number) => (year >= 2400 ? year - 543 : year);

/** The month a word names in any language: the one name it is, else the one month whose names
 * it begins with three letters or more (`toukok`, `SEP`); none when it names none or several. */
function monthOf(word: string): number | undefined {
  const said = bare(word);
  const exact = new Set(
    months()
      .filter(([name]) => name === said)
      .map(([, month]) => month),
  );
  const begun =
    exact.size > 0 || said.length < 3
      ? exact
      : new Set(
          months()
            .filter(([name]) => name.startsWith(said))
            .map(([, month]) => month),
        );
  return begun.size === 1 ? [...begun][0] : undefined;
}

/**
 * The date a text says, as a ticket's PDF writes it: `L 16.05.2026 kell 20.30`, `16/05/26`,
 * `2026-05-16`, `Sat, 16 May 2026`, `May 16th`, `16. toukokuuta`, `16MAY26`, `16.5.`. Without
 * its year, the year that puts it nearest `near`. `null` when it says none.
 */
export function dayIn(text: string, near: IsoDate): IsoDate | null {
  const around = toDate(near) ?? new Date();
  const made = (year: number | undefined, month: number, day: number): IsoDate | null => {
    const years =
      year === undefined
        ? [-1, 0, 1].map((by) => around.getFullYear() + by)
        : [year < 100 ? 2000 + year : commonYear(year)];
    const dates = years
      .map((each) => new Date(each, month - 1, day))
      .filter((date) => date.getMonth() === month - 1 && date.getDate() === day)
      .sort(
        (one, other) =>
          Math.abs(daysBetween(around, one)) - Math.abs(daysBetween(around, other)),
      );
    const date = dates[0];
    return date ? toIso(date) : null;
  };
  const number = (said: string | undefined) => (said === undefined ? undefined : Number(said));
  const ways: [RegExp, (found: RegExpMatchArray) => IsoDate | null][] = [
    [
      // By its units, in any script: `2026年10月4日`, `10月4日`, `2026년 10월 4일`.
      /(?:(\d{4})\s*[年년]\s*)?(\d{1,2})\s*[月월]\s*(\d{1,2})\s*[日일]/gu,
      ([, year, month = '', day = '']) => made(number(year), Number(month), Number(day)),
    ],
    [
      /(?<!\d)(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/gu,
      ([, year = '', month = '', day = '']) => made(Number(year), Number(month), Number(day)),
    ],
    [
      // Day first, unless what is second cannot be a month: `05/16/2026` is American.
      /(?<![\d.:])(\d{1,2})[./-](\d{1,2})[./-](\d{4}|\d{2})(?![\d:])/gu,
      ([, one = '', two = '', year = '']) =>
        Number(two) > 12
          ? made(Number(year), Number(one), Number(two))
          : made(Number(year), Number(two), Number(one)),
    ],
    [
      /(?<![\p{L}\d])(\d{1,2})(?:st|nd|rd|th)?\.?\s*(\p{L}[\p{L}\p{M}.]+)\.?,?(?:\s*(\d{4}|\d{2})(?![\d:.]))?/gu,
      ([, day = '', name = '', year]) => {
        const month = monthOf(name);
        return month === undefined ? null : made(number(year), month, Number(day));
      },
    ],
    [
      /(?<!\p{L})(\p{L}[\p{L}\p{M}.]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?!\d)(?:,?\s+(\d{4}))?/gu,
      ([, name = '', day = '', year]) => {
        const month = monthOf(name);
        return month === undefined ? null : made(number(year), month, Number(day));
      },
    ],
    [
      // A day and a month with their points, no year: `16.5.`, never a clock's `20.30`.
      /(?<![\d.:])(\d{1,2})\.(\d{1,2})\.(?![\d])/gu,
      ([, day = '', month = '']) => made(undefined, Number(month), Number(day)),
    ],
  ];
  for (const [way, read] of ways) {
    // Every place the way finds one, the first that is a date.
    for (const found of text.matchAll(way)) {
      const day = read(found);
      if (day !== null) return day;
    }
  }
  return null;
}
